import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AwardFundingDetails,
  AwardRound,
  AwardRecipient,
  PaginatedResponse,
  SimulationApiResponse,
  GenericPaginatedApiResponse,
  SimulationApiStudent,
  FlagStudentResponse,
  SendApiResponse,
  AcademicYear
} from '@k12/models';
import { SHARED_ENVIRONMENT} from '@k12/app-services';
import { map } from 'rxjs/operators';
import { CwFileTransaction } from './models/cw-file-transaction.model';
import { IneligibilityReason } from './models/ineligibilityReason.model';
import { StudentLedgerTransaction } from './models/student-ledger-transaction.model';

@Injectable({
  providedIn: 'root'
})
export class AwardsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;

  private readonly DEFAULT_PAGE = 1;
  private readonly DEFAULT_PAGE_SIZE = 25;

  public getFundingDetails(academicYearId: number, programTypeId: number): Observable<AwardFundingDetails> {
    return this.http.get<AwardFundingDetails>(
      `${this.apiUrl}/awards/fundingDetails/${programTypeId}/${academicYearId}`
    );
  }

  public getAcademicYears(): Observable<AcademicYear[]> {
    return this.http.get<AcademicYear[]>(`${this.apiUrl}/awards/academicyears`);
  }

  public getCurrentAcademicYear(): Observable<AcademicYear> {
    return this.http.get<AcademicYear>(`${this.apiUrl}/awards/academicyears/current`);
  }

  public simulateAwardByAmount(
    academicYearId: number,
    programTypeId: number,
    amountToAdd: number,
    page: number = this.DEFAULT_PAGE,
    perPage: number = this.DEFAULT_PAGE_SIZE,
    orderBy?: string,
    orderDirection?: string
  ): Observable<PaginatedResponse<AwardRecipient[]>> {
    const params = this.buildPaginationParams(page, perPage, orderBy, orderDirection);

    return this.http
      .get<SimulationApiResponse>(
        `${this.apiUrl}/awards/simulate/amount/${programTypeId}/${academicYearId}/${amountToAdd}`,
        { params }
      )
      .pipe(
        map(
          (resp: SimulationApiResponse): PaginatedResponse<AwardRecipient[]> =>
            this.normalizeSimulationResponse(resp, page, perPage)
        )
      );
  }

  public simulateAwardByTier(
    academicYearId: number,
    tierLevel: number,
    page: number = this.DEFAULT_PAGE,
    perPage: number = this.DEFAULT_PAGE_SIZE,
    orderBy?: string,
    orderDirection?: string
  ): Observable<PaginatedResponse<AwardRecipient[]>> {
    const params = this.buildPaginationParams(page, perPage, orderBy, orderDirection);

    return this.http
      .get<SimulationApiResponse>(`${this.apiUrl}/awards/simulate/osTier/${academicYearId}/${tierLevel}`, { params })
      .pipe(
        map(
          (resp: SimulationApiResponse): PaginatedResponse<AwardRecipient[]> =>
            this.normalizeSimulationResponse(resp, page, perPage)
        )
      );
  }

  public simulateAwardByStudentCount(
    academicYearId: number,
    programTypeId: number,
    numberOfStudents: number,
    page: number = this.DEFAULT_PAGE,
    perPage: number = this.DEFAULT_PAGE_SIZE,
    orderBy?: string,
    orderDirection?: string
  ): Observable<PaginatedResponse<AwardRecipient[]>> {
    const params = this.buildPaginationParams(page, perPage, orderBy, orderDirection);

    return this.http
      .get<SimulationApiResponse>(
        `${this.apiUrl}/awards/simulate/studentcount/${programTypeId}/${academicYearId}/${numberOfStudents}`,
        { params }
      )
      .pipe(
        map(
          (resp: SimulationApiResponse): PaginatedResponse<AwardRecipient[]> =>
            this.normalizeSimulationResponse(resp, page, perPage)
        )
      );
  }

  public simulateAwardByEsaFundingLevel(
    academicYearId: number,
    esaFundingLevel: string,
    page: number = this.DEFAULT_PAGE,
    perPage: number = this.DEFAULT_PAGE_SIZE,
    orderBy?: string,
    orderDirection?: string
  ): Observable<PaginatedResponse<AwardRecipient[]>> {
    const params = this.buildPaginationParams(page, perPage, orderBy, orderDirection);

    return this.http
      .get<SimulationApiResponse>(
        `${this.apiUrl}/awards/simulate/esaFundinglevel/${academicYearId}/${esaFundingLevel}`,
        { params }
      )
      .pipe(
        map(
          (resp: SimulationApiResponse): PaginatedResponse<AwardRecipient[]> =>
            this.normalizeSimulationResponse(resp, page, perPage)
        )
      );
  }

  public simulateAwardByApplicationDateRange(
    academicYearId: number,
    programTypeId: number,
    startDate: string,
    endDate: string,
    page: number = this.DEFAULT_PAGE,
    perPage: number = this.DEFAULT_PAGE_SIZE,
    orderBy?: string,
    orderDirection?: string
  ): Observable<PaginatedResponse<AwardRecipient[]>> {
    let params = this.buildPaginationParams(page, perPage, orderBy, orderDirection);
    params = params.set('startDate', startDate).set('endDate', endDate);

    return this.http
      .get<SimulationApiResponse>(`${this.apiUrl}/awards/simulate/daterange/${programTypeId}/${academicYearId}`, {
        params
      })
      .pipe(
        map(
          (resp: SimulationApiResponse): PaginatedResponse<AwardRecipient[]> =>
            this.normalizeSimulationResponse(resp, page, perPage)
        )
      );
  }

  public sendAwardByAmount(
    academicYearId: number,
    programTypeId: number,
    awardAmount: number
  ): Observable<SendApiResponse> {
    return this.http.put<SendApiResponse>(
      `${this.apiUrl}/awards/send/amount/${programTypeId}/${academicYearId}/${awardAmount}`,
      {}
    );
  }

  public sendAwardByStudentCount(
    academicYearId: number,
    programTypeId: number,
    studentCount: number
  ): Observable<SendApiResponse> {
    return this.http.put<SendApiResponse>(
      `${this.apiUrl}/awards/send/studentcount/${programTypeId}/${academicYearId}/${studentCount}`,
      {}
    );
  }

  public sendAwardByTier(academicYearId: number, tierLevel: number): Observable<SendApiResponse> {
    return this.http.put<SendApiResponse>(`${this.apiUrl}/awards/send/ostier/${academicYearId}/${tierLevel}`, {});
  }

  public getAwardHistoryRounds(academicYearId: number, programTypeId: number): Observable<AwardRound[]> {
    return this.http.get<AwardRound[]>(`${this.apiUrl}/awards/rounds/${programTypeId}/${academicYearId}`);
  }

  public getAwardRecipientsByRound(
    academicYearId: number,
    programTypeId: number,
    roundId: number,
    page: number = this.DEFAULT_PAGE,
    perPage: number = this.DEFAULT_PAGE_SIZE,
    orderBy?: string,
    orderDirection?: string
  ): Observable<PaginatedResponse<AwardRecipient[]>> {
    const params = this.buildPaginationParams(page, perPage, orderBy, orderDirection);

    return this.http
      .get<GenericPaginatedApiResponse>(`${this.apiUrl}/awards/round/${programTypeId}/${academicYearId}/${roundId}`, {
        params
      })
      .pipe(
        map(
          (resp: GenericPaginatedApiResponse): PaginatedResponse<AwardRecipient[]> =>
            this.normalizePaginatedResponse(resp, page, perPage)
        )
      );
  }

  public flagStudent(
    studentId: string,
    academicYearId: number,
    programTypeId: number,
    isFlagged: boolean
  ): Observable<FlagStudentResponse> {
    return this.http.post<FlagStudentResponse>(
      `${this.apiUrl}/awards/flag-student/${programTypeId}/${academicYearId}`,
      {
        studentId,
        isFlagged
      }
    );
  }

  public getClassWalletTransactions(studentId: string, academicYearId: number | null = null): Observable<CwFileTransaction[]> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }
    return this.http.get<CwFileTransaction[]>(`${this.apiUrl}/awards/cwfiles/transactions/${studentId}`, { params });
  }

  public getStudentLedger(studentId: string, academicYearId: number | null = null): Observable<StudentLedgerTransaction[]> {
    const params: Record<string, string | number> = {};
    if (academicYearId !== null) {
      params['academicYearId'] = academicYearId;
    }
    return this.http.get<StudentLedgerTransaction[]>(`${this.apiUrl}/awards/studentledger/${studentId}`, { params });
  }

  public getIneligibilityReasons(): Observable<IneligibilityReason[]> {
    return this.http.get<IneligibilityReason[]>(`${this.apiUrl}/awards/ineligibility-reasons`);
  }

  private buildPaginationParams(page: number, perPage: number, orderBy?: string, orderDirection?: string): HttpParams {
    let params = new HttpParams().set('page', page.toString()).set('perPage', perPage.toString());

    if (orderBy) {
      params = params.set('orderBy', orderBy);
    }
    if (orderDirection) {
      params = params.set('orderDirection', orderDirection);
    }

    return params;
  }

  private normalizeSimulationResponse(
    resp: SimulationApiResponse,
    page: number,
    perPage: number
  ): PaginatedResponse<AwardRecipient[]> {
    const items = resp?.students?.items ?? [];

    // Map simulation API response fields to AwardRecipient model
    const data: AwardRecipient[] = items.map((item: SimulationApiStudent) => ({
      studentId: item.studentId,
      pid: item.pid,
      firstName: item.firstName,
      lastName: item.lastName,
      tier: item.tier ?? 0,
      applicationDate: new Date(item.applicationDate),
      lotteryNumber: item.lotteryNumber ?? '',
      awardAmount: item.awardAmount,
      esaFundingLevel: item.fundingLevel ?? '',
      isOsFlagged: item.isOsFlagged ?? false,
      isEsaFlagged: item.isEsaFlagged ?? false
    }));

    const totalCount = resp?.students?.totalCount ?? 0;
    const pageNumber = resp?.students?.page ?? page;
    const pageSize = resp?.students?.perPage ?? perPage;

    return {
      data,
      totalCount,
      pageNumber,
      pageSize,
      totalAmount: resp?.totalAmount,
      totalStudentCount: resp?.totalStudentCount,
      remainingFunds: resp?.remainingFunds
    };
  }

  private normalizePaginatedResponse(
    resp: GenericPaginatedApiResponse,
    page: number,
    perPage: number
  ): PaginatedResponse<AwardRecipient[]> {
    const data = (resp?.data ?? resp?.items ?? resp?.recipients ?? []) as AwardRecipient[];

    // Try multiple common property names for totalCount
    const totalCountRaw =
      resp?.totalCount ??
      resp?.TotalCount ??
      resp?.total ??
      resp?.Total ??
      resp?.totalRows ??
      resp?.TotalRows ??
      resp?.totalItems ??
      resp?.count;

    // Try multiple common property names for pageSize
    const pageSizeRaw = resp?.perPage ?? resp?.PerPage ?? resp?.pageSize ?? resp?.PageSize ?? resp?.page_size;

    // Try multiple common property names for pageNumber
    const pageNumberRaw = resp?.page ?? resp?.Page ?? resp?.pageNumber ?? resp?.PageNumber ?? resp?.page_number;

    const totalCount = Number.isFinite(Number(totalCountRaw)) ? Number(totalCountRaw) : data.length;
    const pageSizeNorm = Number.isFinite(Number(pageSizeRaw)) ? Number(pageSizeRaw) : perPage;
    const pageNumberNorm = Number.isFinite(Number(pageNumberRaw)) ? Number(pageNumberRaw) : page;

    return {
      data,
      totalCount,
      pageNumber: pageNumberNorm,
      pageSize: pageSizeNorm
    };
  }
}
